import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminHeader from "../../components/Admin/AdminHeader.jsx";
import supportService from "../../services/supportService";
import { MessageSquare, Send, CheckCircle2, Circle, Clock, Search, ChevronRight, XCircle } from "lucide-react";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";

export default function SupportHub() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [filter, setFilter] = useState('all'); // all, open, answered, closed
  const [search, setSearch] = useState('');

  const messagesEndRef = useRef(null);
  const location = useLocation();

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await supportService.getAllTickets(filter === 'all' ? null : filter);
      if (res.success) setTickets(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadMessages = useCallback(async (ticketId) => {
    try {
      const res = await supportService.getTicketMessages(ticketId);
      if (res.success) setMessages(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Gestion de la sélection automatique via URL (ex: ?id=123)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ticketId = params.get('id');
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.id.toString() === ticketId);
      if (ticket) setActiveTicket(ticket);
    }
  }, [tickets, location.search]);

  useEffect(() => {
    if (activeTicket) {
      loadMessages(activeTicket.id);
    }
  }, [activeTicket, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    try {
      const res = await supportService.adminReplyToTicket(activeTicket.id, replyMessage);
      if (res.success) {
        setReplyMessage('');
        loadMessages(activeTicket.id);
        loadTickets(); // Refresh list to update status
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm('Voulez-vous clôturer ce ticket ?')) return;
    try {
      const res = await supportService.updateTicketStatus(activeTicket.id, 'closed');
      if (res.success) {
        loadTickets();
        setActiveTicket(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'open': return <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 text-[10px] rounded-full font-black uppercase tracking-wider flex items-center gap-1"><Circle size={10} className="fill-current"/> En attente</span>;
      case 'answered': return <span className="px-2 py-1 bg-blue-500/10 text-blue-500 text-[10px] rounded-full font-black uppercase tracking-wider">Répondu</span>;
      case 'closed': return <span className="px-2 py-1 bg-emerald-500/10 text-emerald-500 text-[10px] rounded-full font-black uppercase tracking-wider">Fermé</span>;
      default: return null;
    }
  };

  const filteredTickets = tickets.filter(t => 
    (t.subject && t.subject.toLowerCase().includes(search.toLowerCase())) || 
    (t.email && t.email.toLowerCase().includes(search.toLowerCase())) ||
    (t.username && t.username.toLowerCase().includes(search.toLowerCase())) ||
    t.id.toString().includes(search)
  );

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const BASE_URL = API_URL.replace('/api', '');

  return (
    <div className="flex flex-col min-h-screen bg-[#020617] text-slate-200 font-inter">
      <AdminHeader />
      <div className="flex-1 max-w-screen-2xl w-full mx-auto p-4 md:p-8 flex flex-col h-[calc(100vh-80px)] overflow-hidden">
        
        <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-white font-outfit uppercase tracking-wider flex items-center gap-3">
              <MessageSquare className="text-indigo-500" /> Support Hub
            </h1>
            <p className="text-slate-400 font-medium">Gestion des requêtes utilisateurs et assistance technique</p>
          </div>
          <div className="flex gap-2 bg-slate-900/50 p-1 rounded-xl border border-white/5">
            {['all', 'open', 'answered', 'closed'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-colors ${
                  filter === f ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {f === 'all' ? 'Tous' : f === 'open' ? 'En attente' : f === 'answered' ? 'Répondus' : 'Fermés'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 glass-card border-white/5 rounded-3xl overflow-hidden flex flex-col md:flex-row relative">
          {/* List Section */}
          <div className={`w-full md:w-1/3 lg:w-1/4 border-r border-white/5 flex flex-col ${activeTicket ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-white/5 bg-slate-900/50">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input 
                  type="text"
                  placeholder="Rechercher ticket, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
              {loading ? (
                <div className="p-8 text-center text-slate-500 text-sm font-bold uppercase tracking-widest">Chargement...</div>
              ) : filteredTickets.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <p className="text-sm font-bold uppercase tracking-widest">Aucun ticket</p>
                </div>
              ) : (
                filteredTickets.map(ticket => (
                  <button
                    key={ticket.id}
                    onClick={() => setActiveTicket(ticket)}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      activeTicket?.id === ticket.id 
                        ? 'bg-indigo-500/10 border-indigo-500/30' 
                        : 'bg-transparent border-transparent hover:bg-white/5'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs font-bold text-white truncate w-32">#{ticket.id} - {ticket.username}</span>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <div className="text-xs text-slate-400 truncate mb-2">{ticket.subject}</div>
                    <div className="flex justify-between items-center text-[10px] text-slate-500 font-medium">
                      <span className="flex items-center gap-1"><Clock size={10} /> {format(new Date(ticket.updated_at), "dd MMM, HH:mm")}</span>
                      <ChevronRight size={14} className="opacity-50" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Section */}
          {activeTicket ? (
            <div className="flex-1 flex flex-col bg-slate-950/50 relative">
              <div className="p-4 md:p-6 border-b border-white/5 bg-slate-900/50 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <button onClick={() => setActiveTicket(null)} className="md:hidden text-slate-400 hover:text-white">
                     <ChevronRight size={24} className="rotate-180" />
                  </button>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-lg font-black text-white">#{activeTicket.id} - {activeTicket.subject}</h2>
                      {getStatusBadge(activeTicket.status)}
                    </div>
                    <p className="text-xs text-slate-400 font-medium">De: <span className="text-indigo-400">{activeTicket.email}</span> ({activeTicket.username})</p>
                  </div>
                </div>
                {activeTicket.status !== 'closed' && (
                  <button onClick={handleCloseTicket} className="flex items-center gap-2 text-xs font-bold text-rose-500 hover:text-white hover:bg-rose-500 bg-rose-500/10 px-4 py-2 rounded-lg transition-colors border border-rose-500/20 uppercase tracking-widest">
                    <XCircle size={14} /> Clôturer Ticket
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                {messages.map((msg, idx) => {
                  const isAdmin = msg.is_admin_reply;
                  return (
                    <div key={idx} className={`flex flex-col max-w-[85%] ${isAdmin ? 'self-end items-end ml-auto' : 'self-start'}`}>
                       <div className="flex items-center gap-2 mb-1">
                         <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">{isAdmin ? 'Support Admin (Vous)' : 'Client'}</span>
                         <span className="text-[10px] text-slate-600">{format(new Date(msg.created_at), "dd/MM HH:mm")}</span>
                       </div>
                       <div className={`p-4 rounded-2xl ${isAdmin ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-slate-800 border border-white/5 text-slate-200 rounded-tl-none'}`}>
                         <p className="whitespace-pre-wrap text-sm">{msg.message}</p>
                         {msg.attachment_url && (
                           <div className="mt-3">
                             <a href={`${BASE_URL}${msg.attachment_url}`} target="_blank" rel="noreferrer" className="block w-full max-w-sm rounded-lg overflow-hidden border border-white/10 hover:opacity-90 transition-opacity">
                               <img src={`${BASE_URL}${msg.attachment_url}`} alt="Attachment" className="w-full h-auto object-cover" />
                             </a>
                           </div>
                         )}
                       </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>

              {activeTicket.status !== 'closed' ? (
                <div className="p-4 bg-slate-900/80 border-t border-white/5">
                  <form onSubmit={handleReply} className="flex items-end gap-2">
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Réponse officielle de l'administration..."
                      className="flex-1 bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-indigo-500 resize-none min-h-[50px] max-h-[200px] custom-scrollbar"
                      rows="2"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleReply(e);
                        }
                      }}
                    />
                    <button type="submit" disabled={!replyMessage.trim()} className="p-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition-colors disabled:opacity-50 shrink-0 shadow-lg shadow-indigo-500/20">
                      <Send size={20} />
                    </button>
                  </form>
                </div>
              ) : (
                <div className="p-6 bg-slate-900/80 border-t border-white/5 text-center flex flex-col items-center justify-center">
                  <CheckCircle2 size={32} className="text-emerald-500 mb-2 opacity-50" />
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Ticket Clôturé</p>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden md:flex flex-1 items-center justify-center bg-slate-950/50">
              <div className="text-center">
                <MessageSquare size={48} className="mx-auto mb-4 text-slate-800" />
                <p className="text-sm font-black uppercase tracking-widest text-slate-600">Sélectionnez une requête</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
